/**
 * Local storage cache for text analysis results.
 * Caches file content -> vocabulary words to avoid redundant API calls.
 */

import logger from './logger';

const CACHE_KEY_PREFIX = 'vocabCache_';
const CACHE_VERSION = 'v1';

export interface CachedAnalysis {
  words: any[];
  timestamp: number;
  metadata: {
    title?: string;
    author?: string;
    gradeLevel: string;
    subject: string;
    wordCount: number;
  };
  inputLength: number;
  cacheVersion: string;
}

/**
 * Generate a simple hash from a string (djb2 algorithm)
 */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Generate cache key from content and analysis parameters
 */
function getCacheKey(
  content: string,
  gradeLevel: string,
  subject: string,
  wordCount: number
): string {
  const contentHash = simpleHash(content);
  // Include parameters that affect the analysis results
  return `${CACHE_KEY_PREFIX}${contentHash}_${gradeLevel}_${subject}_${wordCount}_${CACHE_VERSION}`;
}

/**
 * Check if cached analysis exists for given content and parameters
 */
export function getCachedAnalysis(
  content: string,
  gradeLevel: string,
  subject: string,
  wordCount: number
): CachedAnalysis | null {
  try {
    const key = getCacheKey(content, gradeLevel, subject, wordCount);
    const cached = localStorage.getItem(key);

    if (!cached) return null;

    const parsed: CachedAnalysis = JSON.parse(cached);

    // Validate cache version
    if (parsed.cacheVersion !== CACHE_VERSION) {
      logger.info('Cache version mismatch, ignoring cached result', {
        cached: parsed.cacheVersion,
        current: CACHE_VERSION,
      });
      localStorage.removeItem(key);
      return null;
    }

    logger.info('Cache hit - using cached analysis', {
      wordCount: parsed.words.length,
      cachedAt: new Date(parsed.timestamp).toISOString(),
    });

    return parsed;
  } catch (error) {
    logger.error('Error reading from analysis cache', error as Error);
    return null;
  }
}

/**
 * Save analysis result to cache
 */
export function setCachedAnalysis(
  content: string,
  gradeLevel: string,
  subject: string,
  wordCount: number,
  words: any[],
  metadata: {
    title?: string;
    author?: string;
    gradeLevel: string;
    subject: string;
    wordCount: number;
  },
  inputLength: number
): void {
  try {
    const key = getCacheKey(content, gradeLevel, subject, wordCount);
    const cached: CachedAnalysis = {
      words,
      timestamp: Date.now(),
      metadata,
      inputLength,
      cacheVersion: CACHE_VERSION,
    };

    localStorage.setItem(key, JSON.stringify(cached));

    logger.info('Cached analysis result', {
      wordCount: words.length,
      inputLength,
    });
  } catch (error) {
    // If localStorage is full or unavailable, just log and continue
    logger.warn('Failed to cache analysis result', { error: String(error) });
  }
}

/**
 * Clear all cached analyses (for cleanup/debugging)
 */
export function clearAnalysisCache(): void {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(CACHE_KEY_PREFIX));

    cacheKeys.forEach(key => localStorage.removeItem(key));

    logger.info('Cleared analysis cache', { count: cacheKeys.length });
  } catch (error) {
    logger.error('Error clearing analysis cache', error as Error);
  }
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { count: number; totalSize: number } {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(CACHE_KEY_PREFIX));

    let totalSize = 0;
    cacheKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += value.length;
      }
    });

    return { count: cacheKeys.length, totalSize };
  } catch (error) {
    logger.error('Error getting cache stats', error as Error);
    return { count: 0, totalSize: 0 };
  }
}

export default {
  getCachedAnalysis,
  setCachedAnalysis,
  clearAnalysisCache,
  getCacheStats,
};
